import { AssetManager, type AssetManagerOptions } from '../assets/AssetManager';
import { type AudioBackend } from '../audio/AudioBackend';
import { AudioManager } from '../audio/AudioManager';
import { DebugService } from '../debug/DebugService';
import { EventEmitter } from '../events/EventEmitter';
import { CursorStyle } from '../input/CursorStyle';
import { InputManager } from '../input/InputManager';
import { PointerLock } from '../input/PointerLock';
import { Fullscreen } from '../rendering/Fullscreen';
import { type Renderer } from '../rendering/Renderer';
import { SceneManager } from '../scene/SceneManager';
import { type EngineEventMap } from './EngineEvents';
import { Time } from './Time';

/**
 * Every engine service, in one object handed to whoever needs them.
 *
 * This is the alternative to singletons: a scene receives the context when it is built, a behavior
 * reaches it through its entity, and nothing anywhere reads a module-level global. Two engines can run
 * on one page, and a test constructs exactly the services it needs with nothing to tear down after.
 *
 * Adding a service to the context is not a breaking change; reaching for a global instead of adding one
 * is an architecture bug.
 */
export interface EngineContext {
  /** The clock for the current frame. */
  readonly time: Time;
  /** The engine event bus. */
  readonly events: EventEmitter<EngineEventMap>;
  /** The graphics backend. */
  readonly renderer: Renderer;
  /** Which scene is running, and how to switch. */
  readonly scenes: SceneManager;
  /** Pointer and keyboard state for the current frame. */
  readonly input: InputManager;
  /** Whether the operating system cursor is shown over the canvas. */
  readonly cursor: CursorStyle;
  /** Whether the pointer is locked to the canvas, and how to ask for that. */
  readonly pointerLock: PointerLock;
  /** Whether the host element fills the screen, and how to ask for that. */
  readonly fullscreen: Fullscreen;
  /** Sound playback and the volume buses. */
  readonly audio: AudioManager;
  /** Textures and sounds, by asset key. */
  readonly assets: AssetManager;
  /** Frame statistics, debug draw and scene introspection. Off until a host turns it on. */
  readonly debug: DebugService;
}

/** Construction options for {@link createEngineContext}. */
export interface EngineContextOptions {
  /** Graphics backend the context exposes and uploads textures through. */
  readonly renderer: Renderer;
  /**
   * Canvas the pointer services act on: `context.cursor` shows and hides the operating system cursor
   * over it, and `context.pointerLock` locks to it. Omit and both do nothing, which is what keeps a
   * test harness free of a DOM.
   */
  readonly pointerTarget?: HTMLElement | undefined;
  /**
   * Element `context.fullscreen` puts on the whole screen, normally `document.documentElement`. Omit and
   * fullscreen reports no support and does nothing.
   *
   * Separate from `pointerTarget` because they are rarely the same element: input is read from the
   * canvas, while filling the screen with the canvas alone would hide every HTML layer beside it.
   */
  readonly fullscreenTarget?: HTMLElement | undefined;
  /** Overrides the audio backend; used by tests and by hosts that own their own `AudioContext`. */
  readonly audioBackend?: AudioBackend | undefined;
  /** Overrides image loading, so assets can be driven with no network and no DOM. */
  readonly loadImage?: AssetManagerOptions['loadImage'];
  /** Overrides byte fetching, for the same reason. */
  readonly loadBytes?: AssetManagerOptions['loadBytes'];
}

/**
 * Builds the standard set of services.
 *
 * Lives beside the interface because the two change together: a new service is a new field here and a
 * new line below, in one place rather than in every caller that assembles a context. {@link Engine}
 * uses it with a real renderer, and the test harness with doubles, so a test can never drift into
 * wiring the services differently from production.
 *
 * Audio and assets are joined here rather than knowing about each other: the asset manager decodes
 * sounds through the audio manager, and the audio manager looks clips up in the asset manager.
 */
export function createEngineContext(options: EngineContextOptions): EngineContext {
  const events = new EventEmitter<EngineEventMap>();
  const audio = new AudioManager({ backend: options.audioBackend });
  const assets = new AssetManager({
    renderer: options.renderer,
    audio,
    loadImage: options.loadImage,
    loadBytes: options.loadBytes,
  });
  audio.setClipProvider(assets);

  return {
    time: new Time(),
    events,
    renderer: options.renderer,
    scenes: new SceneManager(events),
    input: new InputManager(),
    cursor: new CursorStyle(options.pointerTarget),
    pointerLock: new PointerLock({ element: options.pointerTarget, events }),
    fullscreen: new Fullscreen({ element: options.fullscreenTarget, events }),
    audio,
    assets,
    debug: new DebugService(),
  };
}
